import type React from "react";
import { Box, Typography, Button, IconButton, Paper } from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { TruckGroup } from "../../types/truckGroup";
import UserAvatar from "../common/UserAvatar";

interface TruckGroupsPageHeaderProps {
  currentGroup?: TruckGroup;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onInfoClick: () => void;
  canEdit: boolean;
  canDelete: boolean;
}

const TruckGroupsPageHeader: React.FC<TruckGroupsPageHeaderProps> = ({
  currentGroup,
  isEditing,
  onEdit,
  onDelete,
  onInfoClick,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation();

  const showEditButton = () => !(!currentGroup || isEditing);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        mb: 2,
        backgroundColor: "transparent",
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5 }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: "34px",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {currentGroup?.name || "Trucks"}
            </Typography>
            {currentGroup?.user && <UserAvatar user={currentGroup.user} size={20} />}
            {currentGroup?.editorUsers && currentGroup.editorUsers.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <Typography variant="body2" sx={{ mx: 0.25, fontWeight: 700 }}>
                  +
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                  {currentGroup.editorUsers.map((editorUser) => (
                    <UserAvatar key={editorUser._id} user={editorUser} size={18} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("TruckGroup")}
            </Typography>
            <IconButton onClick={onInfoClick} size="small" sx={{ p: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />
            </IconButton>
          </Box>
        </Box>
        {showEditButton() && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              variant="contained"
              size="small"
              onClick={onDelete}
              startIcon={<DeleteIcon />}
              color="error"
              disabled={!canDelete}
            >
              {t("Delete")}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={onEdit}
              startIcon={<EditIcon />}
              disabled={!canEdit}
            >
              {t("Edit")}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TruckGroupsPageHeader;
